import { GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ButtonComponent from '#app/Component/ButtonComponent';
import DataGrid from '#app/Component/DataGrid';
import IconAction from '#app/Component/IconAction';
import ApiHandler from '#app/Handler/ApiHandler';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { StateType } from '#app/Store';
import { transformObjectToDataGridRowsArray } from '#app/Util/DataGridDataTransformer';
import DisputeModal from '#app/View/Sale/OrderClient/SaleOrderClient/components/DisputeModal';
import { ByProduct } from '#app/typings/order';
import {
  DisputeListByClient,
  DisputeState,
  DisputeStateTranslations,
  DisputeType,
  DisputeTypeTranslations,
} from '#app/typings/orderClientLitige';

type Props = {
  products: ByProduct[];
  canOpenDispute?: boolean;
};

const DisputeList: React.FC<Props> = ({ products, canOpenDispute = true }) => {
  const authenticationReducer = useSelector((state: StateType) => state.AuthenticationReducer);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const params = useParams<{ uuid: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [disputes, setDisputes] = useState<DisputeListByClient[]>([]);

  const columns: GridColDef[] = [
    {
      field: 'ref',
      headerName: 'Référence',
      minWidth: 180,
    },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 200,
      flex: 1,
      valueGetter: (value: DisputeType) => DisputeTypeTranslations[value],
    },
    {
      field: 'state',
      headerName: 'Statut',
      minWidth: 150,
      valueGetter: (value: DisputeState) => DisputeStateTranslations[value],
    },
    {
      field: 'reason',
      headerName: 'Raison',
      flex: 1,
    },
    {
      field: 'author',
      headerName: 'Auteur',
      minWidth: 170,
      valueGetter: (_, row: DisputeListByClient) => row.authorLitige?.name,
    },
    {
      field: 'createdAt',
      headerName: 'Date de création',
      minWidth: 150,
      valueGetter: (value) => moment(value).format('lll'),
    },
    {
      field: 'dealAt',
      headerName: 'Date de cloture',
      minWidth: 150,
      valueGetter: (value) => (value ? moment(value).format('lll') : '-'),
    },
    {
      field: 'action',
      headerName: 'Action',
      type: 'actions',
      getActions: ({ row }) => [
        <IconAction
          title="Visualiser"
          icon="view"
          to={getRoutePathname('client_service_order_client_litige', { uuid: row.uuid })}
        />,
      ],
    },
  ];

  useEffect(() => {
    setLoading(true);
    ApiHandler.get<DisputeListByClient[]>(
      {
        route: 'api_sale_order_client_disputes_list',
        params: { uuid: params.uuid as string },
      },
      (response) => {
        setDisputes(response.data ?? []);
        setLoading(false);
      },
    );
  }, [params.uuid]);

  return (
    <>
      <DataGrid
        loading={loading}
        columns={columns}
        rows={transformObjectToDataGridRowsArray(disputes)}
        slots={{
          toolbar:
            canOpenDispute &&
            (authenticationReducer.roles.includes('ROLE_CLIENT_SERVICE_ORDER_CLIENT_LITIGE') ||
              authenticationReducer.roles.includes('ROLE_CLIENT_SERVICE_ORDER_CLIENT_LITIGE_EDIT'))
              ? () => (
                  <GridToolbarContainer>
                    <ButtonComponent color="#F17105" label="Ouvrir un litige" onClick={() => setOpenModal(true)} />
                  </GridToolbarContainer>
                )
              : undefined,
        }}
        hideFooter
        disableColumnSorting
      />
      {openModal && canOpenDispute && <DisputeModal onClose={() => setOpenModal(false)} products={products} />}
    </>
  );
};

export default DisputeList;
